import React, { useMemo, useEffect, useCallback } from 'react';
import intl from 'react-intl-universal';
import { useSelector } from 'react-redux';
import { Typography } from '@getsynapse/design-system';
import { useAppThunkDispatch } from 'state/store';
import {
  selectParticipantIdToRemove,
  setParticipantIdToRemove,
  removeParticipant,
} from 'state/TasksAssignmentsResourcesCapacity/tasksAssignmentsResourcesCapacitySlice';
import { showNotification } from 'state/SnackbarNotification/SnackbarNotificationSlice';
import useModal from 'Hooks/useModal';

const RemoveParticipantModal: React.FC<{ projectId: string }> = ({
  projectId,
}) => {
  const dispatch = useAppThunkDispatch();
  const participantIdToRemove = useSelector(selectParticipantIdToRemove);
  const { Modal, modalProps, openModal, closeModal } = useModal();

  const handleCloseModal = useCallback(() => {
    dispatch(setParticipantIdToRemove(null));
    closeModal();
  }, [closeModal, dispatch]);

  const handleRemoveParticipant = useCallback(async () => {
    try {
      await dispatch(
        removeParticipant({
          project_id: projectId,
          user_id: participantIdToRemove!,
        })
      ).unwrap();
      dispatch(
        showNotification({
          notificationVariant: 'success',
          notificationTitle: intl.get(
            'PEOPLE.RESOURCE_ALLOCATION.REMOVE_PARTICIPANT_MODAL.PARTICIPANT_REMOVED_SUCCESS_SNACKBAR_MESSAGE'
          ),
        })
      );
    } catch (_) {
      dispatch(
        showNotification({
          notificationVariant: 'error',
          notificationTitle: intl.get(
            'PEOPLE.RESOURCE_ALLOCATION.REMOVE_PARTICIPANT_MODAL.PARTICIPANT_REMOVED_ERROR_SNACKBAR_MESSAGE'
          ),
        })
      );
    } finally {
      handleCloseModal();
    }
  }, [dispatch, projectId, participantIdToRemove, handleCloseModal]);

  const actionButons = useMemo(
    () => [
      {
        children: intl.get(
          'PEOPLE.RESOURCE_ALLOCATION.REMOVE_PARTICIPANT_MODAL.REMOVE'
        ),
        variant: 'primary',
        color: 'danger',
        'data-cy': 'remove-button',
        onClick: handleRemoveParticipant,
      },
      {
        children: intl.get('CANCEL'),
        variant: 'tertiary',
        onClick: handleCloseModal,
        'data-cy': 'cancel-button',
      },
    ],
    [handleCloseModal, handleRemoveParticipant]
  );

  useEffect(() => {
    if (participantIdToRemove && !modalProps.isOpen) {
      openModal({
        ...modalProps,
        isOpen: true,
      });
    }
  }, [participantIdToRemove, openModal, modalProps]);

  return (
    <Modal
      {...modalProps}
      title={intl.get(
        'PEOPLE.RESOURCE_ALLOCATION.REMOVE_PARTICIPANT_MODAL.TITLE'
      )}
      titleIcon={{
        name: 'trash',
        className: 'text-error-dark',
      }}
      aria-label={intl.get(
        'PEOPLE.RESOURCE_ALLOCATION.REMOVE_PARTICIPANT_MODAL.TITLE'
      )}
      size='medium'
      className='h-full'
      actionButtons={actionButons}
      data-cy='remove-participant-modal'
      closeModal={handleCloseModal}
    >
      <Typography variant='body' className='text-primary'>
        {intl.getHTML(
          'PEOPLE.RESOURCE_ALLOCATION.REMOVE_PARTICIPANT_MODAL.DESCRIPTION'
        )}
      </Typography>
    </Modal>
  );
};

export default RemoveParticipantModal;
